import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import CenterContainer from "../containers/CenterContainer";
import { registerUser, selectStatus } from "features/signUp/signUpSlice";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { useForm } from "hooks/useForm";
import { selectError } from "features/signUp/signUpSlice";
import SignUpSuccess from "./SignUpSuccess";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  FormControlLabel,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import { CreateAccountRequest } from "api/auth/createAccount";

const SignUpForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const signUpDone = useAppSelector(selectStatus) === "done";
  const signUpLoading = useAppSelector(selectStatus) === "loading";
  const serverErrors = useAppSelector(selectError);
  const [sameEmailForAppleId, setSameEmailForAppleId] = useState<boolean>(true);

  // Using form hook for validation
  const {
    handleSubmit,
    handleChange,
    data: userData,
    errors,
  } = useForm<CreateAccountRequest>({
    validations: {
      signUpCode: {
        required: {
          value: true,
          message: "Ange en registreringskod",
        },
      },
      userName: {
        custom: {
          isValid: (value) => value.length > 2,
          message: "Ange ditt för- och efternam",
        },
      },
      email: {
        pattern: {
          value:
            /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i,
          message: "Ange en giltig e-postadress",
        },
      },
      phone: {
        custom: {
          isValid: (value) => parseInt(value, 10) > 17,
          message: "Ange ett giltigt telefonnummer",
        },
      },
      password: {
        required: {
          value: true,
          message: "Ange ett lösenord",
        },
      },
      confirmPassword: {
        custom: {
          isValid: (value) => {
            return value === userData.password;
          },
          message: "Lösenorden matchar inte",
        },
        required: {
          value: true,
          message: "Ange lösenordet en gång till",
        },
      },
      appleId: {
        required: {
          value: !sameEmailForAppleId,
          message: "Ange ett Apple-ID",
        },
      },
      requestedCompanyName: {
        custom: {
          isValid: (value) => {
            return value !== "";
          },
          message: "Ange företaget du tillhör",
        },
      },
    },
    initialValues: {
      signUpCode: "",
      userName: "",
      email: "",
      phone: 0,
      password: "",
      confirmPassword: "",
      appleId: null,
      requestedCompanyName: "",
    },
    onSubmit: () => {
      dispatch(registerUser(userData));
    },
  });

  return (
    <CenterContainer>
      {signUpDone ? (
        <SignUpSuccess />
      ) : (
        <Card elevation={8} sx={{ p: 4, borderRadius: "1rem", mb: 4 }}>
          <Typography variant="h1" fontSize="2rem" textAlign="center" mb={2}>
            Skapa konto
          </Typography>

          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: "grid", gap: 1 }}>
            <TextField
              variant="filled"
              onChange={handleChange("signUpCode")}
              type="text"
              error={!!errors.signUpCode}
              helperText={errors.signUpCode}
              fullWidth
              label="Registreringskod"
            />

            <TextField
              variant="filled"
              onChange={handleChange("userName")}
              type="text"
              error={!!errors.userName}
              helperText={errors.userName}
              fullWidth
              label="För- och efternamn"
              autoComplete="name"
            />

            <TextField
              variant="filled"
              onChange={handleChange("email")}
              type="email"
              error={!!errors.email}
              helperText={errors.email}
              fullWidth
              label="E-post"
              autoComplete="email"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={sameEmailForAppleId}
                  onChange={() => setSameEmailForAppleId(!sameEmailForAppleId)}
                />
              }
              label="E-posten är samma som mitt Apple-ID"
            />

            {!sameEmailForAppleId && (
              <TextField
                variant="filled"
                onChange={handleChange("appleId")}
                type="text"
                error={!!errors.appleId}
                helperText={errors.appleId}
                fullWidth
                label="Apple-ID"
              />
            )}

            <TextField
              variant="filled"
              onChange={handleChange("phone")}
              type="text"
              error={!!errors.phone}
              helperText={errors.phone}
              fullWidth
              label="Telefon"
              autoComplete="tel"
            />

            <TextField
              variant="filled"
              onChange={handleChange("password")}
              type="password"
              error={!!errors.password}
              helperText={errors.password}
              fullWidth
              label="Lösenord"
              autoComplete="new-password"
            />

            <TextField
              variant="filled"
              onChange={handleChange("confirmPassword")}
              type="password"
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              fullWidth
              label="Bekräfta lösenord"
              autoComplete="new-password"
            />

            <TextField
              variant="filled"
              onChange={handleChange("requestedCompanyName")}
              type="text"
              error={!!errors.requestedCompanyName}
              helperText={errors.requestedCompanyName}
              fullWidth
              label="Företag"
              autoComplete="organization"
            />

            {/* Display validation errors if present */}
            {/* {Object.keys(errors).length > 0 && (
              <Alert severity="error">
                <Box component="ul" mb={0}>
                  {Object.values(errors).map((error, index) => (
                    <Box key={index} component="li">
                      {error}
                    </Box>
                  ))}
                </Box>
              </Alert>
            )} */}

            {/* Display server errors if present */}
            {serverErrors && <Alert severity="error">{serverErrors.message}</Alert>}

            <Button type="submit" variant="contained" loading={signUpLoading} size="large">
              Skapa konto
            </Button>

            <Link component={RouterLink} to="/login" underline="hover" color="primary.dark">
              Har du redan ett konto? Logga in
            </Link>

            <Link component={RouterLink} to="/reset" underline="hover" color="primary.dark">
              Glömt lösenord?
            </Link>
          </Box>
        </Card>
      )}
    </CenterContainer>
  );
};

export default SignUpForm;
